import { useQuery } from "@apollo/client/react";
import { GetAuthData, GetAuthData_me } from "__generated__/types";
import { Amplify } from "aws-amplify";
import { fetchAuthSession } from "aws-amplify/auth";
import { LoadingLayout } from "common/components/layouts/LoadingLayout";
import { Loading } from "common/components/Loading";
import { config } from "common/config";
import gql from "graphql-tag";
import React, { createContext, useContext, useState } from "react";

// Configure authorzation with aws-amplify/auth
Amplify.configure({
  Auth: {
    Cognito: {
      //region: config.cognito.region,
      userPoolClientId: config.cognito.webClientId,
      userPoolId: config.cognito.userPoolId,
      //authenticationFlowType: "USER_SRP_AUTH",
      loginWith: {
        oauth: {
          domain: config.cognito.authDomain,
          scopes: ["email", "openid", "profile"],
          redirectSignIn: [config.cognito.redirectSignIn],
          //redirectUrl: config.cognito.redirectUri,
          redirectSignOut: [config.cognito.redirectSignOut],
          responseType: "code",
        },
      },
    },
  },
});

const GET_AUTH_DATA = gql`
  query GetAuthData {
    me {
      principal {
        user {
          id
          fullName
          hasGivenConsentForProgression
          hasGivenConsentForScores
        }
      }
      menu {
        admin
        feedback
        graphiql
        studies
        teacherFeedback
      }
    }
  }
`;

export interface AuthData {
  isLoggedIn: boolean;
  me: GetAuthData_me | null;
  loading: boolean;
  setSignoutInProgress: () => void;
  refetchAuthData: () => void;
}

const defaultAuthData: AuthData = {
  isLoggedIn: false,
  me: null,
  loading: true,

  setSignoutInProgress: () => {},

  refetchAuthData: () => {},
};

const AuthContext = createContext<AuthData>(defaultAuthData);

export const AuthProvider: React.FC<React.PropsWithChildren> = ({
  children,
}) => {
  const [signoutInProgress, setSignoutInProgress] = useState(false);
  const { loading, data, refetch } = useQuery<GetAuthData>(GET_AUTH_DATA);

  return loading ? (
    <LoadingLayout>
      <Loading />
    </LoadingLayout>
  ) : (
    <AuthContext.Provider
      value={{
        isLoggedIn: data?.me?.principal != null,
        me: data?.me ?? null,
        loading: loading || signoutInProgress,
        setSignoutInProgress: () => {
          setSignoutInProgress(true);
        },
        refetchAuthData: refetch,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthData => {
  const context: AuthData = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("`useAuth` must be within a `UserProvider`");
  }
  return context;
};

export const getAuthorizationTokenJson = async (): Promise<
  Record<string, string>
> => {
  try {
    const { accessToken } = (await fetchAuthSession()).tokens ?? {};
    if (accessToken != null) {
      return {
        Authorization: `Bearer ${accessToken}`,
      };
    } else {
      return {};
    }
  } catch (err) {
    console.log(err);
    return {};
  }
};
/*
export const getAuthorizationTokenJson = async (): Promise<
  Record<string, string>
> => {
  return Auth.currentSession()
    .then((session) => {
      return {
        Authorization: `Bearer ${session.getAccessToken().getJwtToken()}`,
      };
    })
    .catch(() => {
      return {};
    });
};*/
